import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useOption } from '@react-aria/listbox';
import { mergeProps } from '@react-aria/utils';
import { useHover, usePress } from '@react-aria/interactions';
import { ListItemBase } from '../list-item';
import { ListContext } from './contexts';
export const ListItemContext = React.createContext(null);
export const ItemInner = function ItemInner({ item }) {
    const context = useContext(ListContext);
    /* Нужно для того чтобы при мутациях disabledKeys компонент перерисовывался */
    const [_, rerenderTrigger] = useState(false);
    const forceRerender = useCallback(() => rerenderTrigger(t => !t), []);
    if (!context) {
        throw new Error('ItemInner should be placed inside List component');
    }
    const { state, size } = context;
    const setItemEnabled = useCallback(() => {
        state.disabledKeys.delete(item.key);
        forceRerender();
    }, [state, forceRerender]);
    const setItemDisabled = useCallback(() => {
        state.disabledKeys.add(item.key);
        forceRerender();
    }, [state, forceRerender]);
    const itemRef = useRef(null);
    const itemProps = useOption({ key: item.key }, state, itemRef);
    const hoverState = useHover({ isDisabled: itemProps.isDisabled });
    return (React.createElement(ListItemContext.Provider, { value: {
            ...itemProps,
            ...hoverState,
            itemRef,
            size,
            setItemEnabled,
            setItemDisabled,
        } }, item.rendered));
};
export const Item = forwardRef(function Item({ children, disabled, onPress, ...rest }, ref) {
    const context = useContext(ListItemContext);
    if (!context) {
        throw new Error('List.Item should be placed inside List component');
    }
    const { isDisabled, isSelected, optionProps, itemRef, size, isPressed, isHovered, hoverProps, setItemEnabled, setItemDisabled, isFocusVisible, } = context;
    const { pressProps } = usePress({ isDisabled, onPress, isPressed });
    useImperativeHandle(ref, () => itemRef.current);
    useEffect(() => {
        // Здесь именно disabled. Т.е. внешний пропс, а не isDisabled из стейта
        // disabled можно задать на Item, но это для обратной совместимости. Верно дисейблить пропсы через disabledKeys
        if (typeof disabled === 'boolean') {
            if (disabled) {
                setItemDisabled();
            }
            else {
                setItemEnabled();
            }
        }
    }, [disabled]);
    return (React.createElement(ListItemBase, { disabled: isDisabled, pressed: isPressed, focused: isFocusVisible, hovered: isHovered || isSelected, Component: "li", size: size, ...mergeProps(rest, optionProps, hoverProps, pressProps), ref: itemRef }, typeof children === 'function' ? children(context) : children));
});
export const SimpleItem = forwardRef(function Item({ children, onPress, ...rest }, ref) {
    const context = useContext(ListItemContext);
    if (!context) {
        throw new Error('List.Item should be placed inside List component');
    }
    const { isDisabled, optionProps, itemRef, size } = context;
    const { pressProps } = usePress({ isDisabled, onPress });
    return (React.createElement(ListItemBase, { disabled: isDisabled, Component: "li", size: size, ...mergeProps(rest, optionProps, pressProps), ref: itemRef }, children));
});
